import moment from "moment";
import { dailyChecklist, weeklyChecklist } from "./content";

/**
 * Checklist compliance — scans the localStorage history written by the
 * daily and weekly checklists below and computes per-list completion rates.
 *
 * STORAGE — this is a local-only metric for now. The browser is the only
 * record of which boxes were ticked. Roadmap item:
 * `docs/coach-dashboard-roadmap.md` calls out moving this to a backend so
 * it's per-coach and persists across devices/sessions.
 */

const PREFIX = "coach-dashboard:checklist:";

export interface IGroupCompliance {
  groupId: string;
  groupTitle: string;
  itemsChecked: number;
  itemsPossible: number;
  daysWithData: number;
  windowDays: number;
  rate: number | null;
}

function readKey(key: string): Record<string, boolean> | null {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw) as Record<string, boolean>;
  } catch {
    return null;
  }
}

/**
 * Daily groups, evaluated over the last `windowDays` days. Compliance =
 * checked / possible across the whole window, per group. Days with no
 * recorded activity still count toward the denominator — so a coach who
 * never opens the dashboard is correctly at 0%.
 */
/** Load the recorded check-state for each of the last `windowDays` days. */
function loadDailyHistory(windowDays: number): Array<Record<string, boolean>> {
  const today = moment().startOf("day");
  return Array.from({ length: windowDays }, (_, i) => {
    const dayKey = today
      .clone()
      .subtract(i, "day")
      .format("YYYY-MM-DD");
    return readKey(`${PREFIX}${dayKey}`);
  }).filter(v => v !== null) as Array<Record<string, boolean>>;
}

function loadWeeklyHistory(
  windowWeeks: number
): Array<Record<string, boolean>> {
  const now = moment();
  return Array.from({ length: windowWeeks }, (_, i) => {
    const weekKey = now
      .clone()
      .subtract(i, "week")
      .format("GGGG-[W]WW");
    return readKey(`${PREFIX}${weekKey}`);
  }).filter(v => v !== null) as Array<Record<string, boolean>>;
}

export function getDailyCompliance(windowDays = 30): IGroupCompliance[] {
  const history = loadDailyHistory(windowDays);
  return dailyChecklist.map(group => {
    const itemsChecked = history.reduce(
      (sum, day) => sum + group.items.filter(item => day[item.id]).length,
      0
    );
    const itemsPossible = group.items.length * windowDays;
    return {
      groupId: group.id,
      groupTitle: group.title,
      itemsChecked,
      itemsPossible,
      daysWithData: history.length,
      windowDays,
      rate: itemsPossible === 0 ? null : itemsChecked / itemsPossible
    };
  });
}

export function getWeeklyCompliance(windowWeeks = 4): IGroupCompliance {
  const history = loadWeeklyHistory(windowWeeks);
  const itemsChecked = history.reduce(
    (sum, week) => sum + weeklyChecklist.filter(item => week[item.id]).length,
    0
  );
  const itemsPossible = weeklyChecklist.length * windowWeeks;
  return {
    groupId: "weekly",
    groupTitle: "This Week (weekly tasks)",
    itemsChecked,
    itemsPossible,
    daysWithData: history.length,
    windowDays: windowWeeks,
    rate: itemsPossible === 0 ? null : itemsChecked / itemsPossible
  };
}
